import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Popover, Typography, notification } from "antd";
import { BellOutlined, LockOutlined, CheckOutlined, InboxOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getSuspension, getSuspensionMessage } from "../Services/suspension";

const READ_KEY = "notifs_lues";
export const TOAST_KEY = "notif_toast_shown";

const readIds = () => {
  try {
    return JSON.parse(localStorage.getItem(READ_KEY)) || [];
  } catch {
    return [];
  }
};

// Notifications calculées à partir des données de connexion
const buildNotifications = () => {
  const list = [];
  const suspension = getSuspension();
  const { isSuspendu, motif, description, origine } = suspension;
  if (isSuspendu) {
    const lignes =
      origine === "adherent"
        ? [motif && `Motif : ${motif}`, description]
        : [getSuspensionMessage(suspension), description];
    list.push({
      id: `suspension-${origine}-${motif || "x"}`,
      type: "danger",
      icon: <LockOutlined />,
      title:
        origine === "adherent"
          ? "Couverture de votre entreprise suspendue"
          : "Couverture suspendue",
      text: lignes.filter(Boolean).join(" — ") ||
        "La création de documents est temporairement indisponible.",
      link: "/documents",
    });
  }
  return list;
};

const NotificationBell = ({ color = "#114b88" }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [lues, setLues] = useState(readIds);
  const notifs = useMemo(buildNotifications, []);
  const unread = notifs.filter((n) => !lues.includes(n.id));

  const markRead = (ids) => {
    const next = Array.from(new Set([...lues, ...ids]));
    setLues(next);
    try {
      localStorage.setItem(READ_KEY, JSON.stringify(next));
    } catch {}
  };

  // Toast affiché une seule fois par connexion
  useEffect(() => {
    if (!unread.length || localStorage.getItem(TOAST_KEY)) return;
    localStorage.setItem(TOAST_KEY, "1");
    const n = unread[0];
    notification.open({
      message: <span style={{ fontWeight: 700, color: "#a8071a" }}>{n.title}</span>,
      description: n.text,
      icon: <LockOutlined style={{ color: "#f5222d" }} />,
      placement: "topRight",
      duration: 8,
      style: { borderRadius: 14, borderLeft: "5px solid #f5222d" },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const content = (
    <div style={{ width: 340 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Typography.Text style={{ fontWeight: 700, fontSize: 15 }}>Notifications</Typography.Text>
        {unread.length > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => markRead(notifs.map((n) => n.id))}
            style={{ padding: 0, color }}
          >
            Tout marquer comme lu
          </Button>
        )}
      </div>

      {notifs.length === 0 ? (
        <div style={{ padding: "28px 16px", textAlign: "center", color: "#94a3b8" }}>
          <InboxOutlined style={{ fontSize: 30, display: "block", marginBottom: 8 }} />
          Aucune notification
        </div>
      ) : (
        notifs.map((n) => {
          const isUnread = !lues.includes(n.id);
          return (
            <div
              key={n.id}
              onClick={() => {
                markRead([n.id]);
                setOpen(false);
                if (n.link) navigate(n.link);
              }}
              style={{
                display: "flex",
                gap: 12,
                padding: "12px 16px",
                cursor: "pointer",
                background: isUnread ? "#fff1f0" : "transparent",
                transition: "background 0.2s",
              }}
            >
              <span
                style={{
                  flexShrink: 0,
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #ff7875, #f5222d)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                }}
              >
                {n.icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography.Text style={{ display: "block", fontWeight: 600, color: "#0f172a" }}>
                  {n.title}
                </Typography.Text>
                <Typography.Text style={{ fontSize: 12, color: "#64748b" }}>{n.text}</Typography.Text>
              </div>
              {isUnread && (
                <span
                  style={{
                    flexShrink: 0,
                    marginTop: 6,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#f5222d",
                  }}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <>
    <style>{`
      @keyframes ipm-bell { 0%,100% { transform: rotate(0) } 10%,30% { transform: rotate(14deg) } 20%,40% { transform: rotate(-14deg) } 50% { transform: rotate(0) } }
    `}</style>
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      overlayInnerStyle={{ padding: 0, borderRadius: 16, overflow: "hidden" }}
    >
      <Badge count={unread.length} size="small" offset={[-4, 4]}>
        <Button
          shape="circle"
          size="large"
          aria-label="Notifications"
          title="Notifications"
          icon={
            <BellOutlined
              style={{
                fontSize: 20,
                display: "inline-block",
                animation: unread.length ? "ipm-bell 2.4s ease-in-out infinite" : "none",
                transformOrigin: "top center",
              }}
            />
          }
          style={{
            color,
            background: `${color}0d`,
            border: `1px solid ${color}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      </Badge>
    </Popover>
    </>
  );
};

export default NotificationBell;
